/**
 * `<NoteRow>` — single note presentational card with per-note edit + delete
 * (Batch 30j-6; P2 demo-friction fix 2026-06-16).
 *
 * HISTORY: this card was append-only / structurally inert (NO button, NO
 * onClick, NO menu) per the old HoD §10 + Sec HARD-7 stance, which mirrored
 * the backend's append-only spec (threat model T13). The founder explicitly
 * authorized REVERSING T13 on 2026-06-16 — real recruiters make typos and
 * resetting the demo env is unacceptable — adopting a **soft-delete + audit**
 * posture instead. So this card now offers edit + delete affordances.
 *
 * Authz is enforced SERVER-SIDE (author-or-admin). The controls here are a
 * COSMETIC gate only: shown when the viewer authored the note OR is an
 * admin/owner. A recruiter who somehow fires a delete on someone else's note
 * still gets a 403 from the API.
 *
 * Sec HARD-1 (retained): content + the inline editor render content as plain
 * React text nodes — `<p whitespace-pre-wrap>` / a `<textarea>`. NO
 * `dangerouslySetInnerHTML`, NO markdown. React auto-escapes; `<script>` in
 * content surfaces as literal text.
 *
 * Sec HARD-2 (retained): author attribution renders only `"you"` /
 * `"Teammate"` — never a UUID, email, or full name.
 */
import { useState, type JSX } from "react";

import { Button } from "@/components/common/Button";
import { useAuthStore } from "@/stores/useAuthStore";
import { relativeTime, absoluteTime } from "@/lib/relativeTime";
import type { NoteOut } from "@/hooks/queries/notes/useNotes";
import { useDeleteNote } from "@/hooks/queries/notes/useDeleteNote";
import { useUpdateNote } from "@/hooks/queries/notes/useUpdateNote";

const MAX_LEN = 50000;

interface NoteRowProps {
  note: NoteOut;
}

/** Admin/owner/super-admin may act on any note; author on their own. */
function isAdminLike(role: string | null): boolean {
  return role === "admin" || role === "owner" || role === "super_admin";
}

export function NoteRow({ note }: NoteRowProps): JSX.Element {
  const currentUserId = useAuthStore((s) => s.userId);
  const role = useAuthStore((s) => s.role);
  const isCurrentUser =
    currentUserId !== null &&
    currentUserId !== "" &&
    note.user_id === currentUserId;
  // Cosmetic gate — backend is the source of truth (author-or-admin).
  const canAct = isCurrentUser || isAdminLike(role);

  const authorLabel = isCurrentUser ? "you" : "Teammate";
  const rel = relativeTime(note.created_at);
  const abs = absoluteTime(note.created_at);

  const deleteNote = useDeleteNote();
  const updateNote = useUpdateNote();

  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(note.content);

  const startEdit = (): void => {
    setDraft(note.content);
    setIsEditing(true);
  };

  const cancelEdit = (): void => {
    setIsEditing(false);
    setDraft(note.content);
  };

  const saveEdit = (): void => {
    const trimmed = draft.trim();
    if (trimmed === "" || trimmed === note.content) {
      cancelEdit();
      return;
    }
    updateNote.mutate(
      {
        candidate_id: note.candidate_id,
        note_id: note.id,
        content: trimmed,
      },
      { onSuccess: () => setIsEditing(false) },
    );
  };

  const confirmDelete = (): void => {
    deleteNote.mutate({ candidate_id: note.candidate_id, note_id: note.id });
    setConfirmingDelete(false);
  };

  const isPending = updateNote.isPending || deleteNote.isPending;

  return (
    <article
      className="rounded-md border border-border-default bg-surface-card p-4"
      data-testid="note-row"
    >
      <header className="mb-2 flex items-center gap-2">
        <span className="text-sm font-medium text-text-primary">
          {authorLabel}
        </span>
        <span className="text-text-tertiary" aria-hidden="true">
          ·
        </span>
        <time
          dateTime={note.created_at}
          title={abs}
          className="text-xs text-text-tertiary"
        >
          {rel}
        </time>
        {note.edited_at ? (
          <time
            dateTime={note.edited_at}
            title={absoluteTime(note.edited_at)}
            className="text-xs italic text-text-tertiary"
            data-testid="note-edited-indicator"
          >
            (edited)
          </time>
        ) : null}

        {canAct && !isEditing ? (
          <div className="ml-auto flex items-center gap-1">
            <Button
              type="button"
              variant="tertiary"
              size="sm"
              onClick={startEdit}
              disabled={isPending}
              data-testid="note-edit-btn"
            >
              Edit
            </Button>
            <Button
              type="button"
              variant="tertiary"
              size="sm"
              onClick={() => setConfirmingDelete(true)}
              disabled={isPending}
              data-testid="note-delete-btn"
            >
              Delete
            </Button>
          </div>
        ) : null}
      </header>

      {isEditing ? (
        <div className="flex flex-col gap-2" data-testid="note-edit-form">
          <textarea
            rows={4}
            className="min-h-[88px] w-full resize-y rounded-md border border-border-default bg-surface-subtle p-3 text-sm text-text-primary placeholder:text-text-tertiary focus-visible:outline-none focus-visible:border-border-focus focus-visible:shadow-focus disabled:opacity-60 disabled:cursor-not-allowed"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={MAX_LEN}
            disabled={updateNote.isPending}
            data-testid="note-edit-textarea"
          />
          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="tertiary"
              size="sm"
              onClick={cancelEdit}
              disabled={updateNote.isPending}
              data-testid="note-edit-cancel"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={saveEdit}
              disabled={updateNote.isPending || draft.trim() === ""}
              data-testid="note-edit-save"
            >
              {updateNote.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      ) : (
        <p className="whitespace-pre-wrap text-sm text-text-primary">
          {note.content}
        </p>
      )}

      {confirmingDelete ? (
        <div
          className="mt-3 flex items-center justify-between gap-3 rounded-md border border-status-rejected-ring bg-status-rejected-bg p-3"
          data-testid="note-delete-confirm"
        >
          <span className="text-xs text-status-rejected-fg">
            Delete this note? This can't be undone.
          </span>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setConfirmingDelete(false)}
              disabled={deleteNote.isPending}
              data-testid="note-delete-cancel"
            >
              Keep
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={confirmDelete}
              disabled={deleteNote.isPending}
              data-testid="note-delete-confirm-btn"
            >
              {deleteNote.isPending ? "Deleting…" : "Delete"}
            </Button>
          </div>
        </div>
      ) : null}
    </article>
  );
}
